import axios from "axios";

const apiUrl = `${process.env.REACT_APP_API_URL}/api/attendance`;

function getToken() {
  return localStorage.getItem("token");
}

function getAuthHeaders({ required = true } = {}) {
  const token = getToken();

  if (!token) {
    if (required) {
      throw new Error("Utilisateur non authentifié. Token manquant.");
    }

    return {};
  }

  return {
    Authorization: `Bearer ${token}`,
  };
}

function getAxiosConfig({ authRequired = true, params = null } = {}) {
  const headers = getAuthHeaders({ required: authRequired });

  const config = {
    headers,
  };

  if (params) {
    config.params = cleanParams(params);
  }

  return config;
}

function cleanParams(params = {}) {
  return Object.entries(params).reduce((acc, [key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      acc[key] = value;
    }

    return acc;
  }, {});
}

function handleAxiosError(error, fallbackMessage = "Erreur interne du serveur.") {
  if (error?.response?.data) {
    throw error.response.data;
  }

  throw {
    error: error?.message || fallbackMessage,
    message: error?.message || fallbackMessage,
  };
}

export function normalizeDateToYMD(value) {
  if (!value) return "";

  if (typeof value === "string") {
    return value.slice(0, 10);
  }

  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    const year = value.getFullYear();
    const month = String(value.getMonth() + 1).padStart(2, "0");
    const day = String(value.getDate()).padStart(2, "0");

    return `${year}-${month}-${day}`;
  }

  return "";
}

export function getTodayYMD() {
  return normalizeDateToYMD(new Date());
}

function normalizeAttendanceItem(item = {}) {
  const adherentId = item.adherent_id || item.adherentId || item.id;

  return {
    adherent_id: Number(adherentId),
    presence: Boolean(item.presence),
  };
}

export function buildAttendancePayload({ date_presence, attendances = [] }) {
  const finalDate = normalizeDateToYMD(date_presence);

  return {
    date_presence: finalDate,
    attendances: attendances
      .map(normalizeAttendanceItem)
      .filter((item) => Number.isInteger(item.adherent_id) && item.adherent_id > 0),
  };
}

/// GET

// Récupérer toutes les présences
// GET /api/attendance/getAllAttendances
export const getAllAttendances = async () => {
  try {
    const response = await axios.get(
      `${apiUrl}/getAllAttendances`,
      getAxiosConfig({ authRequired: true }),
    );

    return response.data;
  } catch (error) {
    console.error("Erreur lors du chargement de toutes les présences :", error);
    handleAxiosError(error, "Erreur lors du chargement de toutes les présences.");
  }
};

// Récupérer les présences d'un groupe
// GET /api/attendance/group/:groupId
// Options : { date, from, to }
export const getAttendancesByGroup = async (groupId, options = {}) => {
  try {
    const response = await axios.get(
      `${apiUrl}/group/${groupId}`,
      getAxiosConfig({
        authRequired: true,
        params: {
          date: normalizeDateToYMD(options.date),
          from: normalizeDateToYMD(options.from),
          to: normalizeDateToYMD(options.to),
        },
      }),
    );

    return response.data;
  } catch (error) {
    console.error("Erreur lors du chargement des présences du groupe :", {
      groupId,
      options,
      status: error?.response?.status,
      data: error?.response?.data,
    });

    handleAxiosError(error, "Erreur lors du chargement des présences du groupe.");
  }
};

// Récupérer les présences d'un adhérent
// GET /api/attendance/adherent/:adherentId
// Options : { groupId, from, to }
export const getAttendancesByAdherent = async (adherentId, options = {}) => {
  try {
    const response = await axios.get(
      `${apiUrl}/adherent/${adherentId}`,
      getAxiosConfig({
        authRequired: true,
        params: {
          groupId: options.groupId,
          from: normalizeDateToYMD(options.from),
          to: normalizeDateToYMD(options.to),
        },
      }),
    );

    return response.data;
  } catch (error) {
    console.error("Erreur lors du chargement des présences de l'adhérent :", {
      adherentId,
      options,
      status: error?.response?.status,
      data: error?.response?.data,
    });

    handleAxiosError(error, "Erreur lors du chargement des présences de l'adhérent.");
  }
};

// Calculer l'assiduité d'un adhérent
// GET /api/attendance/calculateAssiduity/:adherentId
// Options : { groupId, from, to }
export const calculateAssiduity = async (adherentId, options = {}) => {
  try {
    const response = await axios.get(
      `${apiUrl}/calculateAssiduity/${adherentId}`,
      getAxiosConfig({
        authRequired: true,
        params: {
          groupId: options.groupId,
          from: normalizeDateToYMD(options.from),
          to: normalizeDateToYMD(options.to),
        },
      }),
    );

    return response.data;
  } catch (error) {
    console.error("Erreur lors du calcul de l'assiduité :", {
      adherentId,
      options,
      status: error?.response?.status,
      data: error?.response?.data,
    });

    handleAxiosError(error, "Erreur lors du calcul de l'assiduité.");
  }
};

/// POST

// Enregistrer l'appel complet d'un groupe
// POST /api/attendance/group/:groupId/submit
//
// Payload attendu :
// {
//   date_presence: "2026-05-29",
//   attendances: [
//     { adherent_id: 61, presence: true },
//     { adherent_id: 72, presence: false }
//   ]
// }
export const submitAttendance = async (groupId, payload = {}) => {
  try {
    const finalPayload = buildAttendancePayload(payload);

    if (!groupId) {
      throw new Error("Identifiant de groupe manquant.");
    }

    if (!finalPayload.date_presence) {
      throw new Error("La date de présence est obligatoire.");
    }

    if (!finalPayload.attendances.length) {
      throw new Error("La liste des présences est vide.");
    }

    const response = await axios.post(
      `${apiUrl}/group/${groupId}/submit`,
      finalPayload,
      getAxiosConfig({ authRequired: true }),
    );

    return response.data;
  } catch (error) {
    console.error("Erreur lors de l'enregistrement de l'appel :", {
      groupId,
      payload,
      status: error?.response?.status,
      data: error?.response?.data,
    });

    handleAxiosError(error, "Erreur lors de l'enregistrement de l'appel.");
  }
};

/// PUT

// Mettre à jour une seule présence
// PUT /api/attendance/group/:groupId/adherent/:adherentId/:date
export const setOneAttendance = async ({
  groupId,
  adherentId,
  date,
  presence,
}) => {
  try {
    const finalDate = normalizeDateToYMD(date);

    if (!groupId) {
      throw new Error("Identifiant de groupe manquant.");
    }

    if (!adherentId) {
      throw new Error("Identifiant d'adhérent manquant.");
    }

    if (!finalDate) {
      throw new Error("Date de présence manquante.");
    }

    const response = await axios.put(
      `${apiUrl}/group/${groupId}/adherent/${adherentId}/${finalDate}`,
      {
        presence: Boolean(presence),
      },
      getAxiosConfig({ authRequired: true }),
    );

    return response.data;
  } catch (error) {
    console.error("Erreur lors de la mise à jour d'une présence :", {
      groupId,
      adherentId,
      date,
      presence,
      status: error?.response?.status,
      data: error?.response?.data,
    });

    handleAxiosError(error, "Erreur lors de la mise à jour de la présence.");
  }
};

// Alias court si tu préfères garder le nom côté composant
export const setOne = setOneAttendance;

/// DELETE

// Supprimer une présence précise
// DELETE /api/attendance/group/:groupId/adherent/:adherentId/:date
export const clearOneAttendance = async ({ groupId, adherentId, date }) => {
  try {
    const finalDate = normalizeDateToYMD(date);

    if (!groupId) {
      throw new Error("Identifiant de groupe manquant.");
    }

    if (!adherentId) {
      throw new Error("Identifiant d'adhérent manquant.");
    }

    if (!finalDate) {
      throw new Error("Date de présence manquante.");
    }

    const response = await axios.delete(
      `${apiUrl}/group/${groupId}/adherent/${adherentId}/${finalDate}`,
      getAxiosConfig({ authRequired: true }),
    );

    return response.data;
  } catch (error) {
    console.error("Erreur lors de la suppression d'une présence :", {
      groupId,
      adherentId,
      date,
      status: error?.response?.status,
      data: error?.response?.data,
    });

    handleAxiosError(error, "Erreur lors de la suppression de la présence.");
  }
};

// Alias court
export const clearOne = clearOneAttendance;

// Supprimer toutes les présences
// DELETE /api/attendance/deleteAll
export const deleteAllAttendances = async () => {
  try {
    const response = await axios.delete(
      `${apiUrl}/deleteAll`,
      getAxiosConfig({ authRequired: true }),
    );

    return response.data;
  } catch (error) {
    console.error("Erreur lors de la suppression de toutes les présences :", error);
    handleAxiosError(error, "Erreur lors de la suppression de toutes les présences.");
  }
};